import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle, ChevronRight } from "lucide-react";
import { springs } from "@/components/ui/motion";
import { ChecklistItemRow } from "./ChecklistItemRow";
import type { UIAuditCategory, UIChecklistItem } from "@/packages/types";

interface Step3ReviewChecklistProps {
  categories: UIAuditCategory[];
  auditName: string;
  onUpdateItem: (itemId: string, updates: Partial<UIChecklistItem>) => void;
  onDeleteItem: (itemId: string) => void;
}

function useCountUp(target: number, duration = 400) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (target === 0) return;
    const start = performance.now();
    let frame: number;

    function tick(now: number) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      setValue(Math.round(progress * target));
      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      }
    }

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, duration]);

  return value;
}

export function Step3ReviewChecklist({
  categories,
  auditName,
  onUpdateItem,
  onDeleteItem,
}: Step3ReviewChecklistProps) {
  const totalItems = categories.reduce((sum, cat) => sum + cat.items.length, 0);
  const requiredItems = categories.reduce(
    (sum, cat) => sum + cat.items.filter((i) => i.required).length,
    0,
  );
  const totalCategories = categories.length;

  const animatedTotal = useCountUp(totalItems);
  const animatedRequired = useCountUp(requiredItems);
  const animatedCategories = useCountUp(totalCategories);

  const stats = [
    { label: "items", value: animatedTotal },
    { label: "required", value: animatedRequired },
    { label: "categories", value: animatedCategories },
  ];

  const nonEmptyCategories = categories.filter((cat) => cat.items.length > 0);

  return (
    <div className="flex flex-col gap-4">
      <motion.div
        className="flex items-center gap-3 rounded-xl border border-accent/15 bg-accent/8 p-3"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={springs.smooth}
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={springs.bouncy}
        >
          <CheckCircle className="h-5 w-5 text-accent" />
        </motion.div>
        <div>
          <p className="text-[13px] font-semibold text-primary">
            Checklist ready
          </p>
          <p className="text-[11px] text-secondary">
            Review the generated items below before confirming.
          </p>
        </div>
      </motion.div>

      <div>
        <h3 className="text-sm font-semibold text-primary mb-1">{auditName}</h3>
        <div className="flex items-center gap-2">
          {stats.map((s) => (
            <motion.span
              key={s.label}
              className="rounded-md bg-card px-2 py-0.5 text-[11px] font-medium text-secondary"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ ...springs.gentle, delay: 0.1 }}
            >
              {s.value} {s.label}
            </motion.span>
          ))}
        </div>
      </div>

      <div className="space-y-1">
        {nonEmptyCategories.map((category, index) => (
          <motion.div
            key={category.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              ...springs.smooth,
              delay: 0.15 + index * 0.08,
            }}
          >
            <CategoryGroup
              category={category}
              onUpdateItem={onUpdateItem}
              onDeleteItem={onDeleteItem}
            />
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ─── Category Group ───────────────────────────────────────────────────────────

interface CategoryGroupProps {
  category: UIAuditCategory;
  onUpdateItem: (itemId: string, updates: Partial<UIChecklistItem>) => void;
  onDeleteItem: (itemId: string) => void;
}

function CategoryGroup({
  category,
  onUpdateItem,
  onDeleteItem,
}: CategoryGroupProps) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div>
      <button
        type="button"
        className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-card/50 transition-colors"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <motion.div
          animate={{ rotate: expanded ? 90 : 0 }}
          transition={springs.snappy}
        >
          <ChevronRight className="h-3.5 w-3.5 text-muted" />
        </motion.div>
        <span className="text-xs font-semibold text-primary">
          {category.name}
        </span>
        <span className="text-[10px] text-muted">
          ({category.items.length})
        </span>
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            className="ml-2 overflow-hidden border-l border-border pl-2"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={springs.snappy}
          >
            {category.items.map((item, itemIndex) => (
              <ChecklistItemRow
                key={item.id}
                item={item}
                index={itemIndex}
                onUpdate={onUpdateItem}
                onDelete={onDeleteItem}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
